import {CSSProperties, FC, useEffect, useMemo, useRef, useState} from "react";
import {
    BotSparkle24Regular,
    Checkmark16Regular,
    ChevronDown16Regular,
    Dismiss16Regular,
    LockClosedRegular
} from "@fluentui/react-icons";
import {IInsightAction} from "@/features/coachInsights/api/coachInsightsModels";
import insightActionsStore from "@/features/coachInsights/api/insightActionsStore";
import {useBentoIntelligence} from "../BentoIntelligenceContext";
import {buildWorkflowFromInsightAction} from "./intelligence/workflowBuilders";
import {KIND_ICONS} from "./insightActionIcons";
import AssignControl from "./AssignControl";
import InsightAgentBadge from "./InsightAgentBadge";
import {Assignee, findAssigneeByName} from "../assignableTeam";
import type {AgentIdentity} from "../insightAgents";
import {
    insightCategories,
    type InsightActionCard,
    type InsightCard
} from "../insightCardModels";
import {CATEGORY_TO_PRODUCTS, COACH_BY_ID, KIND_TO_PRODUCTS} from "../coachCatalog";
import {useCoachSettings} from "../coachSettingsStore";
import {useBentoPersona} from "../BentoPersonaContext";
import {
    buildFallbackJustification,
    buildJustificationRequest,
    generateInsightActionJustification
} from "../api/insightActionJustificationAPI";

interface IProps {
    card: InsightCard;
    agent?: AgentIdentity;
    assignedAction?: IInsightAction | null;
    onAssignmentChanged?: () => void;
    onDismiss?: (insightKey: string) => void;
}

function buildInsightAskPrompt(card: InsightCard): string {
    const metric = `${card.headlineMetric.value} — ${card.headlineMetric.label}`;
    return [
        `Give me a root cause analysis of the issues surfaced in this insight card: "${card.title}" (${card.source}).`,
        `Key metric: ${metric}.`,
        card.narrative,
        card.aiNote ? `AI note: ${card.aiNote}` : null,
        card.tags.length ? `Tags: ${card.tags.join(", ")}.` : null
    ]
        .filter((line): line is string => Boolean(line))
        .join("\n\n");
}

function parseEstimatedRoi(value: string): number {
    const match = value.match(/\$\s*([\d,.]+)\s*([kKmM]?)/);
    if (!match) return 0;
    const amount = parseFloat(match[1].replace(/,/g, ""));
    if (Number.isNaN(amount)) return 0;
    const suffix = match[2].toLowerCase();
    if (suffix === "k") return Math.round(amount * 1000);
    if (suffix === "m") return Math.round(amount * 1_000_000);
    return Math.round(amount);
}

const BentoInsightCard: FC<IProps> = ({
    card,
    agent,
    assignedAction,
    onAssignmentChanged,
    onDismiss
}) => {
    const {openWithPrompt, openWorkflow} = useBentoIntelligence();
    const {persona} = useBentoPersona();
    const isNoCoachesPersona = persona.id === "no_coaches_enabled";
    const [expanded, setExpanded] = useState(false);
    const [isExpanding, setIsExpanding] = useState(false);
    const expansionTimer = useRef<number | null>(null);
    const enabledProducts = useCoachSettings();
    const [recommendedActionJustification, setRecommendedActionJustification] =
        useState<string | null>(null);

    const recommendedAction = useMemo(
        () =>
            card.actions.find((action) => action.id === card.recommendedActionId) ??
            card.actions[0],
        [card]
    );

    useEffect(
        () => () => {
            if (expansionTimer.current !== null) {
                window.clearTimeout(expansionTimer.current);
            }
        },
        []
    );

    useEffect(() => {
        let isMounted = true;

        if (!recommendedAction) {
            setRecommendedActionJustification(null);
            return () => {
                isMounted = false;
            };
        }

        const request = buildJustificationRequest(
            card,
            recommendedAction.title,
            recommendedAction.category,
            recommendedAction.description
        );

        generateInsightActionJustification(request)
            .then((justification) => {
                if (!isMounted) return;
                if (justification) {
                    setRecommendedActionJustification(justification);
                    return;
                }

                setRecommendedActionJustification(
                    buildFallbackJustification(
                        recommendedAction.title,
                        recommendedAction.description,
                        request.dollarRoi
                    )
                );
            })
            .catch(() => {
                if (!isMounted) return;
                setRecommendedActionJustification(
                    buildFallbackJustification(
                        recommendedAction.title,
                        recommendedAction.description,
                        request.dollarRoi
                    )
                );
            });

        return () => {
            isMounted = false;
        };
    }, [card, recommendedAction]);

    const handleToggleExpanded = () => {
        if (expanded) {
            setExpanded(false);
            return;
        }

        setIsExpanding(true);

        // This delay does not represent real backend work, but product requested
        // simulated round-trip feedback for the demo.
        const simulatedRoundTripMilliseconds = 550 + Math.random() * 500;
        expansionTimer.current = window.setTimeout(() => {
            setIsExpanding(false);
            setExpanded(true);
            expansionTimer.current = null;
        }, simulatedRoundTripMilliseconds);
    };

    const isProductEnabled = useMemo(() => {
        const category = insightCategories.find((cat) =>
            cat.cards.some((c) => c.id === card.id)
        );
        if (!category) return true;
        const productKeys = CATEGORY_TO_PRODUCTS[category.id];
        if (!productKeys || productKeys.length === 0) return true;
        if (isNoCoachesPersona) return false;
        return productKeys.some((key) => enabledProducts[key]);
    }, [card.id, enabledProducts, isNoCoachesPersona]);

    const AgentIcon = useMemo(() => {
        const category = insightCategories.find((cat) =>
            cat.cards.some((c) => c.id === card.id)
        );
        const productKey = category
            ? CATEGORY_TO_PRODUCTS[category.id]?.[0]
            : undefined;
        if (!productKey) return undefined;
        const [coachId, productId] = productKey.split(":");
        return COACH_BY_ID[coachId]?.products.find(
            (product) => product.id === productId
        )?.icon;
    }, [card.id]);

    const assignee = findAssigneeByName(
        assignedAction?.whoToFollowUpWith?.[0] ?? undefined
    );

    const buildActionPayload = (person?: Assignee) => ({
        insightKey: card.id,
        locationName: "All Locations",
        locationPk: 0,
        businessDate: new Date().toISOString(),
        dayPart: "",
        title: card.title,
        category: card.source,
        estimatedRoi: parseEstimatedRoi(card.headlineMetric.value),
        averageSOS: "",
        targetSOS: "",
        impactedExperiences: 0,
        timeframeStartHour: 0,
        timeframeEndHour: 0,
        whoToFollowUpWith: person ? [person.name] : null,
        selectedActionId: assignedAction?.selectedActionId ?? null
    });

    const handleAssign = async (person: Assignee) => {
        await insightActionsStore.recordAction(
            buildActionPayload(person),
            "AssignToRGM",
            person.isSelf
                ? "Assigned to me"
                : `Assigned to ${person.name} (${person.role})`
        );
        onAssignmentChanged?.();
    };

    const handleRunAction = async (action: InsightActionCard) => {
        if (assignedAction) {
            await insightActionsStore.selectActionPlay(card.id, action.id);
        } else {
            await insightActionsStore.recordAction(
                {...buildActionPayload(), selectedActionId: action.id},
                "AddToFollowUp",
                "Added to Follow Up Tasks"
            );
        }
        onAssignmentChanged?.();
        openWorkflow(buildWorkflowFromInsightAction(action, card, agent));
    };

    const handleUnassign = async () => {
        await insightActionsStore.dismissAction(card.id);
        onAssignmentChanged?.();
    };

    const metricModifier =
        card.headlineMetric.direction === "negative"
            ? "par-insight-metric--negative"
            : card.headlineMetric.direction === "positive"
            ? "par-insight-metric--positive"
            : "";

    return (
        <>
            <div
                className={`par-insight-card${
                    expanded || isExpanding ? " par-insight-card--expanded" : ""
                }${agent ? " par-insight-card--agent" : ""}`}
                style={
                    agent
                        ? ({"--par-agent": agent.accent} as CSSProperties)
                        : undefined
                }
            >
                {/* ── Header ── */}
                <div className="par-insight-header">
                    <div className={`par-insight-metric ${metricModifier}`}>
                        <div className="par-insight-metric__value">
                            {card.headlineMetric.value}
                        </div>
                        <div className="par-insight-metric__label">
                            {card.headlineMetric.label}
                        </div>
                    </div>

                    <div className="par-insight-header__body">
                        {agent && (
                            <InsightAgentBadge
                                icon={
                                    AgentIcon ? (
                                        <AgentIcon />
                                    ) : (
                                        <BotSparkle24Regular />
                                    )
                                }
                                name={agent.name}
                                note="surfaced this insight"
                            />
                        )}
                        <div className="par-insight-header__eyebrow">
                            <span className="par-insight-source">{card.source}</span>
                        </div>
                        <h3 className="par-insight-title">{card.title}</h3>
                        <div className="par-insight-tags">
                            {card.tags.map((tag) => (
                                <span key={tag} className="par-insight-tag">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="par-insight-header__actions">
                        <AssignControl
                            assignee={assignee}
                            onAssign={handleAssign}
                            onUnassign={handleUnassign}
                        />
                        {onDismiss && (
                            <button
                                type="button"
                                className="par-insight-expand"
                                aria-label="Dismiss insight"
                                title="Dismiss insight"
                                onClick={() => onDismiss(card.id)}
                            >
                                <Dismiss16Regular />
                            </button>
                        )}
                        <button
                            type="button"
                            className="par-insight-expand"
                            aria-expanded={expanded}
                            aria-busy={isExpanding}
                            aria-label={
                                isExpanding
                                    ? "Analyzing recommended actions"
                                    : expanded
                                    ? "Collapse insight"
                                    : "Expand insight"
                            }
                            disabled={isExpanding}
                            onClick={handleToggleExpanded}
                        >
                            <ChevronDown16Regular className="par-insight-expand__icon" />
                        </button>
                    </div>
                </div>

                {isExpanding && (
                    <div
                        className="par-insight-expand-loading"
                        role="status"
                        aria-live="polite"
                    >
                        <span
                            className="par-insight-expand-loading__spinner"
                            aria-hidden="true"
                        />
                        <span>Analyzing recommended actions...</span>
                    </div>
                )}

                {expanded && (
                    <>
                        {assignee && (
                            <div className="par-insight-assigned-banner">
                                <span className="par-assign-avatar par-assign-avatar--sm">
                                    {assignee.initials}
                                </span>
                                <span className="par-insight-assigned-banner__text">
                                    {assignee.isSelf ? (
                                        <>
                                            Added to <strong>your tasks</strong> ·
                                            tracked in Follow Up Tasks
                                        </>
                                    ) : (
                                        <>
                                            Assigned to{" "}
                                            <strong>{assignee.name}</strong> ·{" "}
                                            {assignee.role}
                                        </>
                                    )}
                                </span>
                            </div>
                        )}

                        {/* ── Narrative ── */}
                        <p className="par-insight-narrative">{card.narrative}</p>

                        {card.aiNote && (
                            <p className="par-insight-ai-note">
                                {!agent && (
                                    <BotSparkle24Regular className="par-insight-ai-note__icon" />
                                )}
                                <span>
                                    <strong>{agent ? agent.name : "PAR AI"}:</strong>{" "}
                                    {card.aiNote}
                                </span>
                            </p>
                        )}

                        {recommendedActionJustification && (
                            <p className="par-insight-ai-note">
                                {!agent && (
                                    <BotSparkle24Regular className="par-insight-ai-note__icon" />
                                )}
                                <span>
                                    <strong>{agent ? agent.name : "PAR AI"}:</strong>{" "}
                                    {recommendedActionJustification}
                                </span>
                            </p>
                        )}

                        {/* ── Actions ── */}
                        <div className="par-insight-actions">
                            <div className="par-insight-actions__head">
                                <span className="par-insight-section-label">
                                    Actions
                                </span>
                                <button
                                    type="button"
                                    className="par-insight-ask"
                                    onClick={() =>
                                        openWithPrompt(
                                            buildInsightAskPrompt(card),
                                            agent
                                        )
                                    }
                                >
                                    <BotSparkle24Regular className="par-insight-ask__icon" />
                                    {agent
                                        ? `Ask ${agent.name}`
                                        : "Ask about this insight"}
                                </button>
                            </div>

                            {card.actions.map((action) => {
                                const Icon = KIND_ICONS[action.kind];
                                const isChosen =
                                    assignedAction?.selectedActionId === action.id;
                                const isRecommended =
                                    card.recommendedActionId === action.id;
                                const kindKeys = KIND_TO_PRODUCTS[action.kind];
                                const isActionEnabled =
                                    isProductEnabled &&
                                    !isNoCoachesPersona &&
                                    (!kindKeys ||
                                        kindKeys.some(
                                            (key) => enabledProducts[key]
                                        ));
                                const [actionCoachId, actionProductId] =
                                    kindKeys?.[0]?.split(":") ?? [];
                                const actionProduct = actionCoachId
                                    ? COACH_BY_ID[actionCoachId]?.products.find(
                                          (product) => product.id === actionProductId
                                      )
                                    : undefined;
                                const ActionAgentIcon =
                                    actionProduct?.icon ?? AgentIcon;
                                const actionAgentName = actionProduct
                                    ? `${actionProduct.name} Coach`
                                    : agent?.name;
                                return (
                                    <div
                                        key={action.id}
                                        className={`par-action par-action--${
                                            action.kind
                                        }${isChosen ? " par-action--chosen" : ""}${
                                            isRecommended
                                                ? " par-action--recommended"
                                                : ""
                                        }${
                                            !isActionEnabled
                                                ? " par-action--locked"
                                                : ""
                                        }`}
                                    >
                                        <div className="par-action__icon">
                                            <Icon />
                                        </div>
                                        <div className="par-action__body">
                                            <div className="par-action__category">
                                                {action.category}
                                                {isRecommended && (
                                                    <span className="par-action__ai-assist-tag">
                                                        AI Recommends
                                                    </span>
                                                )}
                                                {isChosen && (
                                                    <span className="par-action__chosen-tag">
                                                        <Checkmark16Regular />
                                                        Chosen play
                                                    </span>
                                                )}
                                                {agent && (
                                                    <span className="par-action-agent">
                                                        <span className="par-action-agent__avatar">
                                                            {ActionAgentIcon ? (
                                                                <ActionAgentIcon />
                                                            ) : (
                                                                <BotSparkle24Regular />
                                                            )}
                                                        </span>
                                                        <span className="par-action-agent__name">
                                                            {actionAgentName}
                                                        </span>
                                                        <span className="par-action-agent__found">
                                                            surfaced this action
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                            <div className="par-action__title">
                                                {action.title}
                                            </div>
                                            <div className="par-action__desc">
                                                {action.description}
                                            </div>
                                        </div>
                                        <div className="par-action__cta">
                                            {isActionEnabled ? (
                                                <button
                                                    type="button"
                                                    className="par-action__btn"
                                                    onClick={() =>
                                                        handleRunAction(action)
                                                    }
                                                >
                                                    {action.ctaLabel}
                                                    <span className="par-action__btn-arrow">
                                                        →
                                                    </span>
                                                </button>
                                            ) : (
                                                <span className="par-action__locked-label">
                                                    <LockClosedRegular />
                                                    Not activated
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default BentoInsightCard;
